import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { OrganizationManagementOptions } from '../../../data-contract/organization-management';
import type {
    PositionAccessArea,
    PositionAccessCandidates,
    PositionAccessCoverage,
    PositionAccessGrant,
    PositionAccessGrantList,
} from '../../../data-contract/position-management';
import type {
    CreatePositionAccessGrantInput,
    PositionAccessGrantTarget,
} from '../../../fetchers/position-management/position-management-fetchers';
import { unwrapResult } from '../../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { retryPickerQuery } from '../../api/picker-retry';
import { positionManagementKeys } from '../../api/query-keys';
import { useInvalidatingMutation } from '../../api/use-invalidating-mutation';
import { isAccessCoverageChangedError } from '../../position-error';

const CANDIDATES_LIMIT = 50;

export const usePositionAccessCandidates = ({
    enabled,
    query,
}: {
    readonly enabled: boolean;
    readonly query: string;
}): UseQueryResult<PositionAccessCandidates, Error> => {
    const { searchPositionAccessCandidates } = useApplicationFetchers();

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: ({ signal }) =>
            unwrapResult(searchPositionAccessCandidates({ limit: CANDIDATES_LIMIT, query, signal })),
        queryKey: positionManagementKeys.accessCandidates(query),
        retry: retryPickerQuery,
        throwOnError: false,
    });
};

export const useOrganizationManagementOptions = ({
    enabled,
}: {
    readonly enabled: boolean;
}): UseQueryResult<OrganizationManagementOptions, Error> => {
    const { fetchOrganizationManagementOptions } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: () => unwrapResult(fetchOrganizationManagementOptions()),
        queryKey: positionManagementKeys.organizationOptions(),
        throwOnError: false,
    });
};

export const usePositionAccessCoverage = ({
    architectureId,
    area,
}: {
    readonly architectureId: string;
    readonly area: PositionAccessArea;
}): UseQueryResult<PositionAccessCoverage, Error> => {
    const { fetchPositionAccessCoverage } = useApplicationFetchers();

    return useQuery({
        queryFn: ({ signal }) => unwrapResult(fetchPositionAccessCoverage({ architectureId, request: area, signal })),
        queryKey: positionManagementKeys.accessCoverage(architectureId, area),
        refetchOnWindowFocus: false,
        retry: retryPickerQuery,
        throwOnError: false,
    });
};

export const useCreatePositionAccessGrant = (): UseMutationResult<
    PositionAccessGrant,
    Error,
    CreatePositionAccessGrantInput
> => {
    const { createPositionAccessGrant } = useApplicationFetchers();

    return useInvalidatingMutation(
        createPositionAccessGrant,
        (queryClient) => queryClient.invalidateQueries({ queryKey: positionManagementKeys.accessGrants() }),
        {
            onError: (queryClient, error, { architectureId, request: { kind, path } }) => {
                if (isAccessCoverageChangedError(error)) {
                    void queryClient.invalidateQueries({
                        queryKey: positionManagementKeys.accessCoverage(architectureId, { kind, path }),
                    });
                }
            },
        },
    );
};

export const usePositionAccessGrants = (userId: string): UseQueryResult<PositionAccessGrantList, Error> => {
    const { fetchPositionAccessGrants } = useApplicationFetchers();

    return useQuery({
        queryFn: ({ signal }) => unwrapResult(fetchPositionAccessGrants({ signal, userId })),
        queryKey: positionManagementKeys.accessGrantsOfUser(userId),
        throwOnError: false,
    });
};

export const useRevokePositionAccessGrant = (): UseMutationResult<unknown, Error, PositionAccessGrantTarget> => {
    const { revokePositionAccessGrant } = useApplicationFetchers();

    return useInvalidatingMutation(revokePositionAccessGrant, (queryClient) =>
        queryClient.invalidateQueries({ queryKey: positionManagementKeys.accessGrants() }),
    );
};
